import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import {
  Collapse,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { selectPresentMeta } from '../../../../redux/meta';
import { useAppDispatch } from '../../../../pages/assessment';
import {
  selectMedicalInfoAnswers,
  updateMedicationAndPharmacy,
} from '../../../../redux/assessment';
import { PharmacyForm, PharmacyFormValues } from '../pharmacy/form';
import { PatientPharmacyInput } from '../../../../generated/graphql';
import { AssessmentButtons, AssessmentCard } from '../../shared';
import { rules } from '../../demographics/rules';
import Medications from '../medications';
import OpenToMedicationQuestion from './open-to-medication-question';
import PharmacyQuestion from './pharmacy-question';

export type MedicationPharmacyFormValues = PharmacyFormValues & {
  currentlyTakesMeds: 'Yes' | 'No' | null;
  medications?: string[];
  openToMedication: 'Yes' | 'No' | null;
};

const MedicationPharmacy = () => {
  const dispatch = useAppDispatch();
  const meta = useSelector(selectPresentMeta);
  const medicalInfoAnswers = useSelector(selectMedicalInfoAnswers);

  const [isReadOnly, setIsReadOnly] = useState(false);

  const { addToast } = useToasts();

  const form = useForm<MedicationPharmacyFormValues>({
    mode: 'onBlur',
    defaultValues: {
      currentlyTakesMeds: medicalInfoAnswers?.MI_PrescriptionMedication?.length
        ? medicalInfoAnswers?.MI_PrescriptionMedication[0]
        : null,
      openToMedication: medicalInfoAnswers?.MI_OpenPsychiatricMeds?.length
        ? medicalInfoAnswers?.MI_OpenPsychiatricMeds[0]
        : null,
      hasPharmacy: medicalInfoAnswers?.MI_PrimaryPharmacy?.length
        ? medicalInfoAnswers?.MI_PrimaryPharmacy[0]
        : null,
      medications: medicalInfoAnswers?.MI_MedicationList || [],
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    watch,
    setValue,
    trigger,
  } = form;

  const watchCurrentlyTakesMeds = watch('currentlyTakesMeds');
  const currentlyTakesMeds = watch('currentlyTakesMeds') === 'Yes';

  const [selectedOptionOpenToMeds, setSelectedOptionOpenToMeds] =
    useState(null);

  const handleOnChangeOpenToMeds = option => {
    setSelectedOptionOpenToMeds(option);
  };

  const [selectedOptionHasPharmacy, setSelectedOptionHasPharmacy] =
    useState(null);

  const handleOnChangeOptionForPharm = option => {
    setSelectedOptionHasPharmacy(option);
  };

  useEffect(() => {
    const prevCheckedItems = medicalInfoAnswers?.MI_MedicationPharmacyCombo;
    if (prevCheckedItems?.length > 0) {
      setValue('currentlyTakesMeds', prevCheckedItems[0], {
        shouldValidate: true,
      });
      setIsReadOnly(!meta.canRewrite);
    }
  }, [medicalInfoAnswers, meta?.screen, meta.canRewrite]);

  useEffect(() => {
    const prevCheckedItemsOpenToMeds =
      medicalInfoAnswers?.MI_OpenPsychiatricMeds;

    if (prevCheckedItemsOpenToMeds?.length > 0) {
      setSelectedOptionOpenToMeds(true);
    }

    if (medicalInfoAnswers?.MI_PrimaryPharmacy?.[0] === 'Yes') {
      setSelectedOptionHasPharmacy('Yes');
    }
  }, []);

  const onSubmit = async data => {
    try {
      const existingMedicationList =
        data.medications?.length > 0 ? data.medications : undefined;

      const formattedPhone = data.phone?.replace(/[^0-9]/g, '');

      const pharmacyDetails: PatientPharmacyInput = data.hasPharmacy
        ? {
            name: data.name,
            addressStreet1: data.addressStreet1,
            addressStreet2: data.addressStreet2,
            addressCity: data.addressCity,
            addressState: data.addressState,
            addressZipCode: data.addressZipCode,
            phone: formattedPhone,
          }
        : undefined;

      await dispatch(
        updateMedicationAndPharmacy(
          currentlyTakesMeds,
          existingMedicationList,
          data.openToMedication === 'Yes',
          data.hasPharmacy === 'Yes',
          pharmacyDetails
        )
      );
    } catch (e) {
      addToast(
        e.message ||
          'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AssessmentCard>
        <Controller
          name="currentlyTakesMeds"
          control={control}
          rules={rules.requiredCheckboxOrRadio as any}
          render={({ field, fieldState }) => (
            <FormControl error={!!fieldState.error} disabled={isReadOnly}>
              <RadioGroup {...field}>
                {meta.options?.map((option, index) => (
                  <FormControlLabel
                    control={<Radio />}
                    disabled={isReadOnly}
                    key={`assessment-radio-key${index}`}
                    label={option}
                    value={option}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
        />
      </AssessmentCard>
      <Collapse
        in={currentlyTakesMeds}
        unmountOnExit
        onExited={async () => trigger('currentlyTakesMeds')}
      >
        <Medications control={control} />
      </Collapse>
      {watchCurrentlyTakesMeds !== null && (
        <OpenToMedicationQuestion
          form={form}
          onOptionChange={handleOnChangeOpenToMeds}
        />
      )}
      {selectedOptionOpenToMeds !== null && (
        <PharmacyQuestion
          form={form}
          onOptionChange={handleOnChangeOptionForPharm}
        />
      )}
      {selectedOptionHasPharmacy === 'Yes' && <PharmacyForm form={form} />}
      <AssessmentButtons
        primary={{
          children: 'Continue',
          loading: isSubmitting,
          disabled: !isValid || isSubmitting,
          type: 'submit',
        }}
      />
    </form>
  );
};

export default MedicationPharmacy;
